import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { useMemo } from "react";

import Button from "../../../../components/Button/Button";
import { ButtonTypes } from "../../../../components/Button/types";
import { ErrorState } from "../../../../components/States";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import { DesktopTable } from "./DesktopTable";
import { MobileList } from "./MobileList";
import {
  Footer,
  FooterText,
  LoadMoreLabel,
  LoadMoreWrapper,
  Section,
  SkeletonRow,
} from "./styles";
import { TransactionsListProps } from "./types";
import { groupByDate } from "./utils";

const SkeletonBlock = () => (
  <>
    <SkeletonRow />
    <SkeletonRow />
    <SkeletonRow />
    <SkeletonRow />
  </>
);

export const TransactionsList: React.FC<TransactionsListProps> = ({
  transactions,
  pagination,
  isLoading,
  isLoadingMore,
  error,
  accounts,
  totalCount,
  periodLabel,
  onLoadMore,
  onRetry,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const groups = useMemo(() => groupByDate(transactions), [transactions]);
  const accountIdsByName = useMemo(() => {
    const map = new Map<string, number>();
    for (const account of accounts) {
      map.set(account.name, account.id);
    }
    return map;
  }, [accounts]);

  if (error && transactions.length === 0) {
    return (
      <Section>
        <ErrorState message={error} onRetry={onRetry} />
      </Section>
    );
  }

  if (isLoading && transactions.length === 0) {
    return (
      <Section aria-label="Loading transactions" aria-busy="true">
        <SkeletonBlock />
      </Section>
    );
  }

  if (transactions.length === 0) {
    return null;
  }

  const footerText = `Showing ${transactions.length} of ${pagination.totalRows} in ${periodLabel} · ${totalCount.toLocaleString()} total`;

  return (
    <Section>
      {isCompact ? (
        <MobileList
          groups={groups}
          accountIdsByName={accountIdsByName}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      ) : (
        <DesktopTable
          groups={groups}
          accountIdsByName={accountIdsByName}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      )}
      <Footer>
        <FooterText>{footerText}</FooterText>
        {pagination.hasNext && (
          <LoadMoreWrapper>
            <Button
              type={ButtonTypes.primary}
              onClick={onLoadMore}
              disabled={isLoadingMore}
              isFullWidth={isCompact}
            >
              <LoadMoreLabel>
                Load 10 more
                <KeyboardArrowDownRounded fontSize="small" />
              </LoadMoreLabel>
            </Button>
          </LoadMoreWrapper>
        )}
      </Footer>
    </Section>
  );
};
